import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useUserMap } from '@/features/users/users.hooks';
import { Ticket, User } from '@certhon/models';
import { useMemo } from 'react';

/** Type guard to filter out undefined values with proper type narrowing */
const isDefined = <T,>(value: T | undefined | null): value is T =>
  value !== undefined && value !== null;

interface TicketUserStackCellProps {
  ticket: Ticket;
}

/**
 * Renders the user avatar stack: [Assignee|Executor] badge + associated users.
 * Used as the second column in ticket tables.
 *
 * On mobile: This becomes part of row 2 of the auto-collapsed summary cell.
 * On desktop: This is a regular column.
 */
export function TicketUserStackCell({ ticket }: TicketUserStackCellProps) {
  const userMap = useUserMap('recnum');

  const assigneeUser = useMemo(
    () =>
      ticket.assignee_user_id ? userMap.get(ticket.assignee_user_id) : null,
    [ticket.assignee_user_id, userMap],
  );

  const executorUser = useMemo(
    () =>
      ticket.executor_user_id ? userMap.get(ticket.executor_user_id) : null,
    [ticket.executor_user_id, userMap],
  );

  // Associated users (notification opt-in), excluding assignee/executor to avoid duplicates
  const { associatedUsers, totalAssociatedCount } = useMemo((): {
    associatedUsers: User[];
    totalAssociatedCount: number;
  } => {
    if (!ticket.notification_opt_in || ticket.notification_opt_in.length === 0)
      return { associatedUsers: [], totalAssociatedCount: 0 };
    const excludeIds = new Set([
      ticket.assignee_user_id,
      ticket.executor_user_id,
    ]);
    const filtered = ticket.notification_opt_in
      .filter((id) => !excludeIds.has(id))
      .map((id) => userMap.get(id))
      .filter(isDefined);
    return {
      associatedUsers: filtered.slice(0, 8), // Show max 8 stacked
      totalAssociatedCount: filtered.length,
    };
  }, [
    ticket.notification_opt_in,
    ticket.assignee_user_id,
    ticket.executor_user_id,
    userMap,
  ]);

  // Get all associated users for popover (not just the displayed 8)
  const allAssociatedUsers = useMemo((): User[] => {
    if (!ticket.notification_opt_in || ticket.notification_opt_in.length === 0)
      return [];
    const excludeIds = new Set([
      ticket.assignee_user_id,
      ticket.executor_user_id,
    ]);
    return ticket.notification_opt_in
      .filter((id) => !excludeIds.has(id))
      .map((id) => userMap.get(id))
      .filter(isDefined);
  }, [
    ticket.notification_opt_in,
    ticket.assignee_user_id,
    ticket.executor_user_id,
    userMap,
  ]);

  // Don't render anything if no users
  if (!assigneeUser && !executorUser && associatedUsers.length === 0) {
    return null;
  }

  const stackedUsers = (
    <div className="isolate flex flex-shrink-0 items-center gap-1">
      {/* Assignee/Executor grouped badge - always reserves space for both */}
      <div className="bg-muted/50 ring-border z-20 flex items-center rounded-full p-0.5 ring-1">
        {/* Assignee slot - on top (z-10) */}
        {assigneeUser ? (
          <div className="z-10 flex h-6 w-6 items-center justify-center">
            <UserAvatar
              recnum={assigneeUser.recnum}
              name={assigneeUser.name}
              loginname={assigneeUser.loginname}
              size="sm"
              className="ring-card ring-2"
            />
          </div>
        ) : (
          <div className="z-10 h-6 w-6" />
        )}
        {/* Executor slot - behind assignee (z-0) */}
        {executorUser ? (
          <div className="z-0 -ml-2 flex h-6 w-6 items-center justify-center">
            <UserAvatar
              recnum={executorUser.recnum}
              name={executorUser.name}
              loginname={executorUser.loginname}
              size="sm"
              className="ring-card ring-2"
            />
          </div>
        ) : (
          <div className="z-0 -ml-2 h-6 w-6" />
        )}
      </div>
      {/* Associated users stack/overlap */}
      {associatedUsers.map((user, index) => (
        <UserAvatar
          key={user.recnum}
          recnum={user.recnum}
          name={user.name}
          loginname={user.loginname}
          size="sm"
          className="ring-card -ml-3 ring-2 first:ml-0"
          style={{ zIndex: 5 - index }}
        />
      ))}
      {totalAssociatedCount > 8 && (
        <span
          className="bg-muted ring-card z-0 -ml-3 flex h-6 w-6 items-center justify-center
            rounded-full text-xs ring-2"
        >
          +{totalAssociatedCount - 8}
        </span>
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default">{stackedUsers}</div>
      </TooltipTrigger>
      <TooltipContent
        className="bg-popover text-popover-foreground hidden border shadow-md md:block"
        arrowClassName="bg-popover fill-popover"
        align="start"
        sideOffset={4}
      >
        <div className="flex max-w-64 flex-col gap-3 p-1">
          {/* Assignee */}
          {assigneeUser && (
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center">
                <UserAvatar
                  recnum={assigneeUser.recnum}
                  name={assigneeUser.name}
                  loginname={assigneeUser.loginname}
                  size="sm"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {assigneeUser.name || assigneeUser.loginname}
                </span>
                <span className="text-muted-foreground text-xs">Assignee</span>
              </div>
            </div>
          )}
          {/* Executor */}
          {executorUser && (
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center">
                <UserAvatar
                  recnum={executorUser.recnum}
                  name={executorUser.name}
                  loginname={executorUser.loginname}
                  size="sm"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {executorUser.name || executorUser.loginname}
                </span>
                <span className="text-muted-foreground text-xs">Executor</span>
              </div>
            </div>
          )}
          {/* Following */}
          {allAssociatedUsers.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-muted-foreground text-xs">Following</span>
              <div className="flex flex-wrap gap-1">
                {allAssociatedUsers.map((user) => (
                  <div
                    key={user.recnum}
                    className="bg-muted/50 flex items-center gap-1.5 rounded-full py-0.5 pr-2 pl-0.5"
                  >
                    <UserAvatar
                      recnum={user.recnum}
                      name={user.name}
                      loginname={user.loginname}
                      size="sm"
                    />
                    <span className="text-xs">
                      {user.name || user.loginname}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
